import React from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useFunnel } from '../../contexts/FunnelContext'
import { CheckCircle, Sparkles } from 'lucide-react'
import { PrimaryButton } from '../../components/funnel/PrimaryButton'
import { CountdownTimer } from '../../components/funnel/CountdownTimer'

const RefreshConfirmedOfferPage: React.FC = () => {
  const navigate = useNavigate()
  const { state } = useFunnel()

  const handleUpgrade = () => {
    navigate('/founding-member-checkout')
  }

  const handleDecline = () => {
    navigate('/winter-refresh/thank-you')
  }

  return (
    <div style={{ backgroundColor: 'rgb(246, 246, 241)', minHeight: '100vh', padding: '2rem 0' }}>
      <div className="mx-auto max-w-4xl px-8">
        {/* Section 1: Purchase Confirmation */}
        <div className="bg-white p-8 rounded-xl mb-8 text-center">
          <CheckCircle className="w-20 h-20 mx-auto mb-4" style={{ color: '#145F6B' }} />
          <h2 className="text-3xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
            Your Home Refresh is Booked!
          </h2>
          <p className="text-lg mb-2" style={{ color: 'rgb(76,76,75)' }}>
            Thank you for your purchase. Our team will call you within one business day to confirm
            your appointment.
          </p>
          <p className="text-base" style={{ color: 'rgb(76,76,75)' }}>
            You will receive your detailed report and $100 gift card after the service is complete.
          </p>
        </div>

        {/* Visual Separator */}
        <div className="h-px w-full mb-8" style={{ backgroundColor: '#dee2e6' }} />

        {/* Section 2: Exclusive Founding Member Offer */}
        <div
          className="bg-white p-8 rounded-xl border-2 shadow-lg"
          style={{ borderColor: '#9B775D', backgroundColor: 'rgb(253, 245, 230)' }}
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-6 h-6" style={{ color: '#9B775D' }} />
              <h1 className="text-3xl font-bold" style={{ color: 'rgb(15,15,15)' }}>
                Wait! As a New Customer, You're Invited to Become a Founding Member
              </h1>
            </div>
            <p className="text-lg font-semibold" style={{ color: 'rgb(15,15,15)' }}>
              Lock in our lowest-ever rate for complete, year-round peace of mind. This is a
              one-time offer.
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="mb-8">
            <CountdownTimer initialMinutes={15} />
          </div>

          {/* Value Proposition Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-6 text-center" style={{ color: 'rgb(15,15,15)' }}>
              Upgrade to Effortless Homeownership
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column: Your Home Refresh */}
              <div className="bg-white p-6 rounded-xl border-2" style={{ borderColor: '#dee2e6' }}>
                <h3 className="text-xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
                  Your One-Time Service
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: 'rgb(76,76,75)' }}>
                  <li>• Home Health Check (Mini Snapshot)</li>
                  <li>
                    • {state.selectedPackage === 'deluxe' ? 'All 6' : 'Choose 3'} Seasonal
                    Maintenance Tasks
                  </li>
                  <li>• Mini MyHome Score™ Summary Report</li>
                  <li>• One-time service only</li>
                </ul>
              </div>

              {/* Right Column: Founding Membership - Highlighted */}
              <div
                className="bg-white p-6 rounded-xl border-2 relative"
                style={{ borderColor: '#9B775D', borderWidth: '3px' }}
              >
                <div
                  className="absolute -top-3 right-6 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: '#9B775D', color: 'white' }}
                >
                  RECOMMENDED
                </div>
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#145F6B' }}>
                  Year-Round Membership
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: 'rgb(15,15,15)' }}>
                  <li className="font-semibold">• Everything in your Refresh, PLUS:</li>
                  <li>• Monthly Proactive Home Checkups</li>
                  <li>• Dedicated Home Director</li>
                  <li>• Annual & 5-Year Home Planning</li>
                  <li>• Included Fix-It Hours Every Quarter</li>
                  <li>• 24/7 Concierge Service for All Home Needs</li>
                  <li>• MyHome Vault™ & Blueprint™ Access</li>
                  <li>• $300 Annual Supply Credit</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Call-to-Action Section */}
          <div className="bg-white p-8 rounded-xl border-2 text-center" style={{ borderColor: '#145F6B' }}>
            <h2 className="text-2xl font-semibold mb-6" style={{ color: 'rgb(15,15,15)' }}>
              Lock In Your Founding Member Rate
            </h2>
            <div className="mb-6">
              <div className="flex items-center justify-center gap-4 mb-2">
                <span
                  className="text-2xl line-through"
                  style={{ color: 'rgb(76,76,75)' }}
                >
                  $399/month
                </span>
                <span className="text-4xl font-bold" style={{ color: '#9B775D' }}>
                  $199/month
                </span>
              </div>
              <p className="text-lg font-semibold" style={{ color: '#145F6B' }}>
                (You save over $2,400 per year!)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleUpgrade}
                className="px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105"
                style={{ backgroundColor: '#9B775D', color: 'white' }}
              >
                Yes! Upgrade Me to a Founding Member
              </button>
              <button
                onClick={handleDecline}
                className="px-8 py-4 text-base font-medium"
                style={{ color: 'rgb(76,76,75)' }}
              >
                No thanks, I'll stick with the one-time service
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RefreshConfirmedOfferPage

